import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { DataTestID } from 'src/utils';

import { CustomTooltip } from '../tooltip/CustomTooltip';
import { IChipWithCustomTooltip } from './ChipWithCustomTooltip.interface';
import { StyledChip } from './ChipWithCustomTooltip.styled';

export const ChipWithCustomTooltip = ({
  className,
  tooltipTitle,
  tooltipWidth,
  tooltipPlacement,
  tooltipActivationWidth,
  chipName,
  color,
  variant,
  size,
  onDelete,
  onClick,
  replaceSpaces = true,
  removeBackslashes = false,
  name,
  isMatched = true,
  forceTooltip = false,
}: IChipWithCustomTooltip) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);

  const formatText = (
    text: string,
    replaceSpaces: boolean,
    removeBackslashes: boolean,
  ) => {
    let result = text;

    if (replaceSpaces) {
      result = result?.replace(/ /g, '\u00a0');
    }
    if (removeBackslashes) {
      result = result?.replace(/\\/g, '');
    }

    return result;
  };

  useEffect(() => {
    if (!ref.current || !tooltipActivationWidth || !chipName) {
      setOverflow(false);

      return;
    }

    const labelElement = ref.current.querySelector(
      '.MuiChip-label',
    ) as HTMLElement;
    if (!labelElement) {
      setOverflow(false);

      return;
    }

    const naturalWidth = labelElement.scrollWidth;
    setOverflow(naturalWidth > tooltipActivationWidth);
  }, [tooltipActivationWidth, chipName, name]);

  return overflow || forceTooltip ? (
    <CustomTooltip
      width={tooltipWidth}
      placement={tooltipPlacement || 'bottom'}
      title={formatText(
        tooltipTitle as string,
        replaceSpaces,
        removeBackslashes,
      )}
      skipValidateOverflow={true}>
      <StyledChip
        className={className}
        data-testid={DataTestID.CustomChip}
        ref={ref}
        variant={variant}
        $overflow={overflow}
        $tooltipActivationWidth={tooltipActivationWidth}
        label={formatText(chipName as string, replaceSpaces, removeBackslashes)}
        color={color}
        size={size}
        {...(onDelete && { onDelete: () => onDelete(chipName) })}
        {...(onClick && {
          onClick: (event: MouseEvent<HTMLDivElement>) => onClick(event),
        })}
        $isNotMatched={!isMatched}
        icon={isMatched ? undefined : <WarningAmberIcon fontSize="small" />}
      />
    </CustomTooltip>
  ) : (
    <StyledChip
      $tooltipActivationWidth={tooltipActivationWidth}
      ref={ref}
      className={className}
      data-testid={DataTestID.CustomChip}
      label={formatText(chipName as string, replaceSpaces, removeBackslashes)}
      variant={variant}
      color={color}
      size={size}
      {...(onDelete && { onDelete: () => onDelete(chipName) })}
      {...(onClick && {
        onClick: (event: MouseEvent<HTMLDivElement>) => onClick(event),
      })}
      $isNotMatched={!isMatched}
      icon={isMatched ? undefined : <WarningAmberIcon fontSize="small" />}
    />
  );
};
